import { create } from "zustand";
import { produce } from "immer";
import { useMemo } from "react";

import type { DeviceAnswers } from "../domain/eating";
import type { TrainingDevice } from "../domain/programme";
import { calibrateStride, LIVE_BASELINE_TOLERANCE_MS, measuredInWindow } from "../domain/energy";
import { walkTotals } from "../domain/walk";
import { easyTreadmillLine } from "../domain/trainingQuestions";
import { playerMode, type PlayerMode } from "../domain/playerView";
import { addDays, todayStr } from "../domain/date";
import { neutralDay } from "../domain/plan";
import { earnableFreezes } from "../domain/progress";
import { sessionStale } from "../domain/session";
import { dayActive, freshBadges, missedDays, stats } from "../domain/stats";
import type { ActiveSession, Badge, KayaState, StepSample } from "../domain/types";
import {
  loadMeta,
  loadState,
  saveMeta,
  saveState,
  type EatingDraft,
  type EatingSafety,
  type LocalMeta,
  type PlayerPrefs,
  type TextSize,
  type TrainingDraft,
  type TrainingSafety,
} from "../storage/local";
import { api, ApiError, getToken, setToken, type AuthUser, type StepDay } from "../sync/api";
import { mergeState } from "../sync/merge";
import { drinkIsNew, withDrinks, type PendingDrink } from "../domain/water";
import { clearDrinks, queuedDrinks } from "../lib/drinkQueue";
import { defaultState, hydrate } from "./defaults";
import { applyDrinks } from "./toggles";

type Mutator = (draft: KayaState) => void;

/**
 * Runs a writer over the state and returns the next one.
 *
 * The writer's return value is dropped on purpose. Several writers return a count or a flag —
 * `copyDayFood`, `repeatMeal`, `logPlanned` — and a page passes them straight through
 * (`update((d) => copyDayFood(d, y, today))`). Immer reads any return as "here is the new state"
 * and throws when the draft was modified as well, which is what made Copy yesterday, Repeat and
 * logging a planned meal fail silently. Replacing the state whole is `replaceAll`, never a return.
 */
function run(S: KayaState, fn: Mutator): KayaState {
  return produce(S, (d) => {
    fn(d);
  });
}

export type SyncStatus = "offline" | "idle" | "syncing" | "error" | "signed-out";

/**
 * How often the phone's step figure is re-pulled while the app is open.
 *
 * The counter app samples every fifteen minutes at best, so asking more often than that only
 * re-reads the same row. The deadline is checked against the wall clock rather than counted in
 * ticks, because a backgrounded tab's timers are throttled — the same reason `useTimer` works
 * the way it does.
 */
const STEP_REFRESH_MS = 15 * 60 * 1000;
/** A refresh on every visibility flap would hammer the server when the counter app pops over
 *  the browser and back; one a minute is plenty. */
const STEP_REFRESH_MIN_GAP_MS = 60 * 1000;

/** Random enough for one phone's handful of sessions; readable in a log. */
function newSid(): string {
  return Math.random().toString(36).slice(2, 8) + Date.now().toString(36);
}

type SessionSeed = Omit<ActiveSession, "sid" | "banked" | "since"> &
  Partial<Pick<ActiveSession, "banked" | "since">>;


export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface KayaStore {
  S: KayaState;
  meta: LocalMeta;
  ready: boolean;

  user: AuthUser | null;
  online: boolean;
  syncStatus: SyncStatus;
  syncError: string | null;
  lastSync: number | null;

  toast: string | null;
  /** the toast's one action — Undo after a drink (ADR-044) — or null for a plain message */
  toastAction: ToastAction | null;
  badgeQueue: Badge[];

  /**
   * The session open on this device — a walk, a treadmill run or a workout — or null.
   *
   * Mirrors `meta.session` so screens can select it. Every Start button reads it first and goes
   * to the open session instead of beginning another; the players rebuild their clock from it on
   * mount; the app lands on its route at launch. (ADR-027)
   */
  session: ActiveSession | null;
  beginSession: (seed: SessionSeed) => ActiveSession;
  patchSession: (fn: (s: ActiveSession) => void) => void;
  endSession: () => void;
  /** Puts the Today setup checklist away on this device, for good. */
  dismissSetup: () => void;
  /** "Later" on Today's setup notice: hidden until the app is next opened. Not persisted. */
  setupSnoozed: boolean;
  snoozeSetup: () => void;
  /** Records on this device that the checklist's treadmill question has been answered. */
  answerSetupTreadmill: () => void;
  /** Sets this phone's text size (kit §40) and applies it at once; never synced. */
  setTextSize: (size: TextSize) => void;
  /** The questionnaire's safety answer, on this phone only (ADR-042); undefined takes it back. */
  setEatingSafety: (answer: EatingSafety | undefined) => void;
  /** Keeps an unfinished questionnaire on this phone; undefined drops it. */
  setEatingDraft: (draft: EatingDraft | undefined) => void;
  /** The exercise safety screens' answer, on this phone only (ADR-050); undefined takes it back. */
  setTrainingSafety: (answer: TrainingSafety | undefined) => void;
  /** Keeps an unfinished "Plan my exercise" on this phone; undefined drops it. (ADR-053) */
  setTrainingDraft: (draft: TrainingDraft | undefined) => void;
  /** Train's "first one back" notice, put away for the day on this phone (ADR-065) */
  setBreakNoticeOff: (ds: string) => void;
  /** The player's Simple mode, voice and language on this phone (ADR-050); undefined resets one. */
  setPlayerPrefs: (prefs: PlayerPrefs) => void;
  /** A walk's planned length, and the noticed walks said no to, on this phone (ADR-063). */
  setWalkMeta: (prefs: Partial<Pick<LocalMeta, "walkPlanMin" | "noticedOff">>) => void;

  /** epoch ms the phone last posted today's figure; null when it has not today */
  stepsReportedAt: number | null;
  /** epoch ms this device last pulled step data, whichever path did it */
  stepsPulledAt: number | null;
  /**
   * Pulls the phone's step data — day totals, today's readings, and any session window the
   * readings now cover — and folds it into the state without pushing anything.
   *
   * The full sync did this too, but only ran on launch, on an edit, or on coming back online, so
   * a phone opened at four in the afternoon showed the figure from breakfast until something else
   * happened to change. This runs on its own: on every return to the app and every quarter hour
   * while it is open, and behind the Refresh button. (ADR-027)
   */
  refreshSteps: (opts?: { force?: boolean }) => Promise<void>;

  init: () => Promise<void>;
  /** Mutate state immer-style, persist locally, then sync in the background. */
  update: (fn: Mutator, opts?: { silent?: boolean }) => void;
  replaceAll: (s: KayaState) => void;
  /** Saves `next` as the state and schedules a sync — the shared tail of `update` and `replaceAll`. */
  commit: (next: KayaState, opts?: { silent?: boolean }) => void;

  /** A message for ~2 s; with `action`, a button on it and `ms` (default 5 s) to use it. */
  showToast: (m: string, opts?: { action?: ToastAction; ms?: number }) => void;
  clearToast: () => void;
  dismissBadge: () => void;

  checkBadges: (silent?: boolean) => void;
  applyFreezes: () => void;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  /** Fills a blank profile name from the signed-in account. */
  adoptAccountName: () => void;
  /**
   * Whether an admin is currently looking at the app as a normal user would.
   *
   * Device-local rather than part of the synced blob: it is a preview, and following the owner
   * onto their phone would be a puzzle rather than a feature. It hides admin surfaces in the app
   * and nothing more — the token is unchanged, so this previews the interface, not the server's
   * permissions. (ADR-020)
   */
  viewAsUser: boolean;
  setViewAsUser: (v: boolean) => void;

  /** Re-asks the server who this is — how the waiting screen notices it has been approved. */
  refreshUser: () => Promise<AuthUser | null>;
  acceptUser: (token: string, user: AuthUser) => Promise<void>;
  /** Keeps the account in memory and on disk, so an offline launch still knows who is signed in. */
  rememberUser: (user: AuthUser) => void;
  logout: () => void;
  syncNow: (opts?: { force?: boolean }) => Promise<void>;
  /** Folds in the glasses the service worker queued from a notification's +250 ml (ADR-045). */
  absorbDrinks: () => Promise<void>;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;
/** An edit landed while a sync was out. The sync merges it in and asks for another to push it. */
let editedDuringSync = false;

const VIEW_AS_KEY = "kaya-view-as";

function loadViewAsUser(): boolean {
  try {
    return localStorage.getItem(VIEW_AS_KEY) === "user";
  } catch {
    return false;
  }
}

export const useKaya = create<KayaStore>((set, get) => ({
  S: defaultState(),
  meta: { updatedAt: 0 },
  ready: false,

  user: null,
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  syncStatus: "signed-out",
  syncError: null,
  lastSync: null,

  toast: null,
  toastAction: null,
  badgeQueue: [],
  viewAsUser: loadViewAsUser(),

  session: null,
  setupSnoozed: false,
  stepsReportedAt: null,
  stepsPulledAt: null,

  beginSession(seed) {
    const s: ActiveSession = { banked: 0, since: Date.now(), ...seed, sid: newSid() };
    const meta = { ...get().meta, session: s };
    set({ session: s, meta });
    void saveMeta(meta);
    return s;
  },

  patchSession(fn) {
    const cur = get().session;
    if (!cur) return;
    const next = produce(cur, fn);
    const meta = { ...get().meta, session: next };
    set({ session: next, meta });
    void saveMeta(meta);
  },

  endSession() {
    if (!get().session) return;
    const meta = { ...get().meta, session: null };
    set({ session: null, meta });
    void saveMeta(meta);
  },

  dismissSetup() {
    const meta = { ...get().meta, setupDismissedAt: Date.now() };
    set({ meta });
    void saveMeta(meta);
  },

  snoozeSetup() {
    set({ setupSnoozed: true });
  },

  answerSetupTreadmill() {
    if (get().meta.setupTreadmillAnswered) return;
    const meta = { ...get().meta, setupTreadmillAnswered: true };
    set({ meta });
    void saveMeta(meta);
  },

  setTextSize(size) {
    const meta = { ...get().meta, textSize: size };
    set({ meta });
    void saveMeta(meta);
    applyTextSize(size);
  },

  setEatingSafety(answer) {
    const meta = { ...get().meta, eatingSafety: answer };
    set({ meta });
    void saveMeta(meta);
  },

  setEatingDraft(draft) {
    const meta = { ...get().meta, eatingDraft: draft };
    set({ meta });
    void saveMeta(meta);
  },

  setTrainingSafety(answer) {
    const meta = { ...get().meta, trainingSafety: answer };
    set({ meta });
    void saveMeta(meta);
  },

  setTrainingDraft(draft) {
    const meta = { ...get().meta, trainingDraft: draft };
    set({ meta });
    void saveMeta(meta);
  },

  setBreakNoticeOff(ds) {
    const meta = { ...get().meta, breakNoticeOff: ds };
    set({ meta });
    void saveMeta(meta);
  },

  setPlayerPrefs(prefs) {
    const meta: LocalMeta = { ...get().meta };
    for (const k of ["simplePlayer", "voice", "lang"] as const) {
      if (!(k in prefs)) continue;
      if (prefs[k] === undefined) delete meta[k];
      else Object.assign(meta, { [k]: prefs[k] });
    }
    set({ meta });
    void saveMeta(meta);
  },

  setWalkMeta(prefs) {
    const meta: LocalMeta = { ...get().meta, ...prefs };
    set({ meta });
    void saveMeta(meta);
  },

  async refreshSteps(opts) {
    const { user, online, syncStatus, stepsPulledAt } = get();
    if (!user || user.status !== "approved" || !online) return;
    // The full sync pulls the same rows; running both at once would race on the same fields.
    if (syncStatus === "syncing") return;
    const now = Date.now();
    if (!opts?.force && stepsPulledAt && now - stepsPulledAt < STEP_REFRESH_MIN_GAP_MS) return;
    set({ stepsPulledAt: now });

    try {
      const today = todayStr();
      const dayStart = new Date(today + "T00:00:00").getTime();
      const [days, todays, covering] = await Promise.all([
        api.pullSteps(addDays(today, -30), today),
        api.pullSamples(dayStart, dayStart + 24 * 3600 * 1000),
        pullCovering(unmeasuredWindows(get().S)),
      ]);

      // Folded into the state as it is *now*, not as it was when the requests went out — the
      // user may have logged something in between, and this must not put it back.
      fold((d) => {
        applyStepDays(d, days.days);
        d.sampleCache = { date: today, samples: todays.samples };
        if (covering.length) applyMeasurements(d, covering);
      });
      set({ stepsReportedAt: reportedToday(days.days, today) });
    } catch {
      /* the figure on screen stays what it was; the next return to the app tries again */
    }
  },

  setViewAsUser(v) {
    set({ viewAsUser: v });
    try {
      if (v) localStorage.setItem(VIEW_AS_KEY, "user");
      else localStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* blocked storage — the toggle then lasts for this session only */
    }
  },

  async init() {
    const [S, loaded] = await Promise.all([loadState(), loadMeta()]);
    // A walk begun by a build that kept it in the synced blob is carried over once, so updating
    // mid-walk does not lose it. The blob's copy is then cleared: the merge never carried it.
    let meta: LocalMeta =
      !loaded.session && S.activeWalk
        ? {
            ...loaded,
            session: {
              kind: "walk",
              sid: newSid(),
              route: "/walk",
              startedAt: S.activeWalk.startedAt,
              banked: S.activeWalk.banked,
              since: S.activeWalk.since,
            },
          }
        : loaded;
    // A record left from yesterday is a session somebody forgot, not one to land on.
    if (meta.session && sessionStale(meta.session, Date.now())) meta = { ...meta, session: null };
    if (meta !== loaded) void saveMeta(meta);

    // The cached account stands in until the server answers. Behind a sign-in gate this is what
    // lets a plane-mode launch open the app rather than the login screen.
    set({ S, meta, ready: true, user: meta.user ?? null, session: meta.session ?? null });
    if (S.activeWalk) {
      fold((d) => {
        d.activeWalk = null;
      });
    }

    // apply theme and text size before first paint of the app shell
    applyTheme(S.profile.theme);
    applyTextSize(meta.textSize);

    if (getToken()) {
      try {
        const user = await get().refreshUser();
        if (user?.status === "approved") {
          await get().syncNow({ force: true });
          get().adoptAccountName();
        }
      } catch (e) {
        // An expired token signs out; anything else is the network, and the cached account
        // stands, because being offline is not the same as being signed out.
        if (e instanceof ApiError && e.status === 401) get().logout();
        else set({ syncStatus: "offline" });
      }
    } else if (meta.user) {
      // token gone but a cached account left behind — treat it as signed out
      set({ user: null, syncStatus: "signed-out" });
      void saveMeta({ ...meta, user: undefined });
    }

    get().applyFreezes();
    get().checkBadges(true);
    void get().absorbDrinks();

    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        set({ online: true });
        void get().syncNow();
      });
      window.addEventListener("offline", () => set({ online: false, syncStatus: "offline" }));

      // Coming back to the app is the moment the step figure is looked at, so it is the moment
      // to fetch it. Three events because no single one fires on every path back: switching
      // apps, returning from the counter app's invisible bridge, and a page restored from the
      // back-forward cache each announce themselves differently.
      const wake = () => {
        if (document.visibilityState !== "visible") return;
        void get().refreshSteps();
        void get().absorbDrinks();
      };
      // The worker says so when a notification's +250 ml queued a drink while this page was open.
      navigator.serviceWorker?.addEventListener("message", (e: MessageEvent) => {
        if ((e.data as { type?: string } | null)?.type === "drinks") void get().absorbDrinks();
      });
      document.addEventListener("visibilitychange", wake);
      window.addEventListener("focus", wake);
      window.addEventListener("pageshow", wake);
      window.setInterval(() => {
        const last = get().stepsPulledAt ?? 0;
        if (document.visibilityState === "visible" && Date.now() - last >= STEP_REFRESH_MS)
          void get().refreshSteps();
      }, 60_000);
    }
  },

  update(fn, opts) {
    get().commit(run(get().S, fn), opts);
  },

  replaceAll(s) {
    get().commit(s);
  },

  commit(next, opts) {
    const meta = { ...get().meta, updatedAt: Date.now() };
    set({ S: next, meta });
    void saveState(next);
    void saveMeta(meta);
    if (get().syncStatus === "syncing") editedDuringSync = true;

    applyTheme(next.profile.theme);

    if (!opts?.silent) {
      // debounce: a workout writes on every step, and each write need not hit the network
      if (syncTimer) clearTimeout(syncTimer);
      syncTimer = setTimeout(() => void get().syncNow(), 2500);
    }
  },

  showToast(m, opts) {
    const action = opts?.action ?? null;
    set({ toast: m, toastAction: action });
    setTimeout(
      () => {
        if (get().toast === m && get().toastAction === action) set({ toast: null, toastAction: null });
      },
      opts?.ms ?? (action ? 5000 : 1900)
    );
  },

  clearToast() {
    set({ toast: null, toastAction: null });
  },

  dismissBadge() {
    set({ badgeQueue: get().badgeQueue.slice(1) });
  },

  checkBadges(silent) {
    const S = get().S;

    // freezes first — earning one can be what keeps a streak-based badge alive
    const earned = earnableFreezes(S);
    if (earned.length) {
      get().update((d) => {
        earned.forEach((k) => (d.freezeEarned[k] = 1));
        d.freezes = Math.min(3, d.freezes + earned.length);
      });
      if (!silent)
        get().showToast(`+${earned.length} streak freeze${earned.length > 1 ? "s" : ""} earned`);
    }

    const fresh = freshBadges(get().S);
    if (fresh.length) {
      const earnedOn = todayStr();
      get().update((d) => {
        fresh.forEach((b) => (d.badges[b.id] = earnedOn));
      });
      if (!silent) set({ badgeQueue: [...get().badgeQueue, ...fresh] });
    }
  },

  applyFreezes() {
    const S = get().S;
    const missed = missedDays(S);
    if (!missed.length) return;

    let spent = 0;
    get().update((d) => {
      missed.forEach((ds) => {
        if (d.freezes > 0) {
          d.freezes--;
          if (!d.logs[ds]) d.logs[ds] = { done: {}, treadmill: null };
          d.logs[ds].frozen = true;
          spent++;
        }
      });
    });
    if (spent) get().showToast("Streak freeze used — streak protected");
  },

  async login(email, password) {
    const { token, user } = await api.login(email, password);
    await get().acceptUser(token, user);
  },

  async register(email, password, name) {
    const { token, user } = await api.register(email, password, name);
    await get().acceptUser(token, user);
  },

  /** Shared tail of signing in and signing up: a pending account gets a session but no sync,
   *  because every state and steps endpoint would refuse it anyway. */
  async acceptUser(token, user) {
    setToken(token);
    get().rememberUser(user);
    set({ syncStatus: "idle", syncError: null });
    if (user.status !== "approved") return;
    await get().syncNow({ force: true });
    get().adoptAccountName();
  },

  rememberUser(user) {
    const meta = { ...get().meta, user, userId: user.id };
    set({ user, meta });
    void saveMeta(meta);
  },

  async refreshUser() {
    try {
      const { user } = await api.me();
      get().rememberUser(user);
      if (user.status !== "approved") set({ syncStatus: "signed-out" });
      return user;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        get().logout();
        return null;
      }
      throw e;
    }
  },

  /**
   * Borrows the name from the account for the profile, when the profile has none.
   *
   * Signing up asks for a name and then never used it: Home greeted a blank, and Profile said
   * "Set your name" to someone who had just typed it. Only fills a gap — a profile name that has
   * been set is the user's own choice and outranks whatever the account was registered under,
   * and this runs after the sync so a name already on the server is never overwritten either.
   */
  adoptAccountName() {
    const { user, S } = get();
    if (!user || S.profile.name.trim()) return;
    const name = user.name.trim();
    if (!name) return;
    get().update((d) => {
      d.profile.name = name;
    });
  },

  logout() {
    setToken(null);
    // The checklist belongs to the account: a second one signing in on this phone gets its own.
    const meta = {
      ...get().meta,
      rev: undefined,
      userId: undefined,
      user: undefined,
      setupDismissedAt: undefined,
      setupTreadmillAnswered: undefined,
      // the person's, not the phone's: the next account to sign in here must not inherit them
      eatingSafety: undefined,
      eatingDraft: undefined,
      trainingSafety: undefined,
      trainingDraft: undefined,
    };
    set({
      user: null,
      meta,
      setupSnoozed: false,
      syncStatus: "signed-out",
      syncError: null,
      lastSync: null,
    });
    void saveMeta(meta);
  },

  async absorbDrinks() {
    const queued = await queuedDrinks();
    if (!queued.length) return;
    // A drink the person tapped is their own entry, so it is stamped like any other edit.
    if (queued.some((d) => drinkIsNew(get().S, d))) get().update((d) => applyDrinks(d, queued));
    await clearDrinks(queued.map((d) => d.id));
  },

  async syncNow(opts) {
    const { user, online, S, meta } = get();
    if (!user || user.status !== "approved") {
      set({ syncStatus: "signed-out" });
      return;
    }
    if (!online) {
      set({ syncStatus: "offline" });
      return;
    }
    if (get().syncStatus === "syncing" && !opts?.force) return;

    set({ syncStatus: "syncing", syncError: null });
    editedDuringSync = false;
    try {
      const remote = await api.pull();
      // The server stores the blob verbatim, so it may predate fields this build expects
      // (or come from another client). Fill the gaps before merging rather than trusting it.
      const mergedBlob = remote.state
        ? mergeState(
            { state: S, updatedAt: meta.updatedAt },
            { state: hydrate(remote.state), updatedAt: remote.updatedAt }
          )
        : S;
      // Glasses logged from a notification wait in the server's inbox (ADR-045). They go into
      // the state pushed now and are acknowledged only once that push has landed, so a failure
      // anywhere in between leaves them in the inbox for the next sync rather than losing them.
      const inbox = await api.drinkInbox().then((r) => r.drinks, () => [] as PendingDrink[]);
      const merged = inbox.length ? withDrinks(mergedBlob, inbox) : mergedBlob;

      const stamp = Math.max(meta.updatedAt, remote.updatedAt) || Date.now();
      const pushed = await api.push(merged, stamp);
      if (inbox.length) void api.drinkAck(inbox.map((d) => d.id)).catch(() => undefined);

      // Steps live outside the state blob because automations write them, so they are
      // pulled separately and folded in locally.
      const pulled = await pullStepsInto(merged);
      const withSteps = await cacheTodaySamples(await measureTreadmillsIn(pulled.S));

      // An edit made while the requests were out lives in `get().S` and nowhere else. Merging
      // it with what came back keeps it — and the sync its own debounce asked for found this one
      // running and gave up, so another is scheduled below to push it.
      const cur = get().meta;
      const kept = editedDuringSync
        ? {
            ...mergeState(
              { state: get().S, updatedAt: cur.updatedAt },
              { state: withSteps, updatedAt: pushed.updatedAt }
            ),
            // the merge does not carry the day's readings; they were just fetched
            sampleCache: withSteps.sampleCache,
          }
        : withSteps;
      // `cur`, not the `meta` read before the awaits: the session may have been paused or ended
      // in between and must not be rolled back by a sync that never touched it.
      const nextMeta = {
        ...cur,
        updatedAt: editedDuringSync ? Math.max(cur.updatedAt, pushed.updatedAt) : pushed.updatedAt,
        rev: pushed.rev,
        userId: user.id,
      };
      set({
        S: kept,
        meta: nextMeta,
        syncStatus: "idle",
        lastSync: Date.now(),
        stepsPulledAt: Date.now(),
        stepsReportedAt: pulled.reportedAt,
      });
      await saveState(kept);
      // The latest, again: a record patched while the state was saving must win on disk too.
      await saveMeta(get().meta);
      if (editedDuringSync) {
        if (syncTimer) clearTimeout(syncTimer);
        syncTimer = setTimeout(() => void get().syncNow(), 2500);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        get().logout();
        return;
      }
      // 403 means the account is no longer allowed to sync — approval was revoked, or the token
      // outlived it. Re-ask who we are so the gate can show the reason instead of "Sync failed".
      if (e instanceof ApiError && e.status === 403) {
        set({ syncStatus: "signed-out", syncError: e.message });
        void get().refreshUser().catch(() => {});
        return;
      }
      set({ syncStatus: "error", syncError: e instanceof Error ? e.message : "Sync failed" });
    }
  },
}));

/**
 * Applies a mutation to the state as it stands now and saves it, without stamping `updatedAt`.
 *
 * For data that arrived from the server rather than from the user: stamping it would make this
 * device look like the more recently edited one and let its settings outrank another phone's
 * genuine edits on the next merge. The sync's own fold has never stamped, and this is the same
 * fold on a different trigger.
 */
function fold(fn: Mutator): void {
  const next = run(useKaya.getState().S, fn);
  useKaya.setState({ S: next });
  void saveState(next);
}

/** A reading may land a little either side of a window; this is how far to look. */
const WINDOW_PAD_MS = 15 * 60 * 1000;
/** Windows are pulled one request each, so the count is bounded. Newest first: those are the
 *  ones the readings can still cover. */
const MAX_WINDOWS = 12;

interface Window {
  fromMs: number;
  toMs: number;
}

/**
 * Sessions with both ends and no measurement yet, from the last two weeks, each as the span of
 * readings that would cover it.
 *
 * One span per session rather than one from the oldest to now: a live session posts every ten
 * seconds, and the server answers the oldest four thousand readings, so a single span stretching
 * back a fortnight came back without today's walk in it — the one that was being asked for.
 */
function unmeasuredWindows(S: KayaState): Window[] {
  const since = addDays(todayStr(), -14);
  const out: Window[] = [];
  const add = (startedAt: string, at: string) =>
    out.push({
      fromMs: Date.parse(startedAt) - Math.max(WINDOW_PAD_MS, LIVE_BASELINE_TOLERANCE_MS),
      toMs: Date.parse(at) + WINDOW_PAD_MS,
    });
  for (const [ds, L] of Object.entries(S.logs)) {
    if (ds < since) continue;
    const t = L.treadmill;
    if (t && t.startedAt && t.at && typeof t.measuredSteps !== "number") add(t.startedAt, t.at);
    for (const w of L.walks || [])
      if (w.startedAt && w.at && typeof w.measuredSteps !== "number") add(w.startedAt, w.at);
  }
  return out.sort((a, b) => b.fromMs - a.fromMs).slice(0, MAX_WINDOWS);
}

async function pullCovering(windows: Window[]): Promise<StepSample[]> {
  if (!windows.length) return [];
  const results = await Promise.all(windows.map((w) => api.pullSamples(w.fromMs, w.toMs)));
  const byT = new Map<number, StepSample>();
  for (const r of results) for (const s of r.samples) byT.set(s.t, s);
  return [...byT.values()];
}

/**
 * Fills in `measuredSteps` for treadmill sessions and free walks whose window the readings cover,
 * and lets a trustworthy distance teach the stride.
 *
 * Only sessions that have both ends and no measurement yet are considered, and only the last two
 * weeks, so this is a bounded amount of work. A session that cannot be measured is left alone
 * rather than marked zero — `stepBreakdown` then falls back to asking, which is the pre-existing
 * behaviour.
 */
export function applyMeasurements(d: KayaState, samples: StepSample[]): void {
  const since = addDays(todayStr(), -14);
  for (const [ds, L] of Object.entries(d.logs)) {
    if (ds < since) continue;

    const t = L.treadmill;
    if (t && t.startedAt && t.at && typeof t.measuredSteps !== "number") {
      const measured = measuredInWindow(samples, Date.parse(t.startedAt), Date.parse(t.at));
      if (measured !== null) {
        t.measuredSteps = measured;
        // Only a distance we can actually trust may teach the stride. A session logged as
        // "used the machine's own program" carries Kaya's protocol distance, not the distance
        // the machine actually covered — calibrating from that would divide the wrong distance
        // by real steps and learn a wrong stride. It qualifies only once the console figure
        // has been entered.
        const trustworthy = t.protocol !== "preset" || t.kmFromConsole === true;
        const cal = trustworthy ? calibrateStride(d, t.km, measured) : null;
        if (cal) {
          d.profile.strideM = cal.strideM;
          d.profile.strideSamples = cal.strideSamples;
        }
      }
    }

    for (const w of L.walks || []) {
      if (!w.startedAt || !w.at || typeof w.measuredSteps === "number") continue;
      const measured = measuredInWindow(samples, Date.parse(w.startedAt), Date.parse(w.at));
      if (measured === null) continue;
      w.measuredSteps = measured;
      // With real steps the pace is known, so the calorie figure logged from a nominal MET can
      // be replaced with one based on how fast this walk actually was.
      const tot = walkTotals(d, w.secs, measured);
      w.km = tot.km ?? undefined;
      w.kcal = tot.kcal;
      // A distance the user vouched for is the treadmill case without the treadmill: known
      // distance, counted steps. A derived `km` could never do this — it came from the stride.
      if (typeof w.kmDeclared === "number") {
        const cal = calibrateStride(d, w.kmDeclared, measured);
        if (cal) {
          d.profile.strideM = cal.strideM;
          d.profile.strideSamples = cal.strideSamples;
        }
      }
    }
  }
}

async function measureTreadmillsIn(S: KayaState): Promise<KayaState> {
  try {
    const samples = await pullCovering(unmeasuredWindows(S));
    if (!samples.length) return S;
    return produce(S, (d) => applyMeasurements(d, samples));
  } catch {
    // Measuring is an enhancement; failing to do it must never fail the sync.
    return S;
  }
}

/**
 * Keeps today's readings so the hourly chart has something to draw.
 *
 * Today only: a rolling history of these in the state blob would add thousands of rows to every
 * sync for a chart nobody scrolls back through.
 */
async function cacheTodaySamples(S: KayaState): Promise<KayaState> {
  try {
    const today = todayStr();
    const start = new Date(today + "T00:00:00").getTime();
    const { samples } = await api.pullSamples(start, start + 24 * 3600 * 1000);
    return produce(S, (d) => {
      d.sampleCache = { date: today, samples };
    });
  } catch {
    return S; // a chart is not worth failing a sync over
  }
}

/** Folds server-side step totals into the day logs. Never overwrites a number the user
 *  typed in by hand — that lives in `steps`, this lands in `deviceSteps`. Exported for the family
 *  view, which folds a linked person's rows the same way (ADR-046). */
export function applyStepDays(d: KayaState, days: StepDay[]): void {
  days.forEach((row) => {
    if (!d.logs[row.date]) d.logs[row.date] = { done: {}, treadmill: null };
    d.logs[row.date].deviceSteps = row.steps;
    d.logs[row.date].deviceStepsSource = row.source.startsWith("ios")
      ? "ios-health"
      : row.source.startsWith("android")
        ? "android-sensor"
        : "manual-import";
  });
}

/** When the phone last posted today's row — the honest answer to "is this figure current?". */
function reportedToday(days: StepDay[], today: string): number | null {
  const row = days.find((r) => r.date === today);
  return row && row.updatedAt > 0 ? row.updatedAt : null;
}

async function pullStepsInto(
  S: KayaState
): Promise<{ S: KayaState; reportedAt: number | null }> {
  try {
    const to = todayStr();
    const from = addDays(to, -120);
    const { days } = await api.pullSteps(from, to);
    if (!days.length) return { S, reportedAt: null };
    return { S: produce(S, (d) => applyStepDays(d, days)), reportedAt: reportedToday(days, to) };
  } catch {
    // a step pull failing must not fail the state sync
    return { S, reportedAt: null };
  }
}

function applyTheme(theme: KayaState["profile"]["theme"]) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

/**
 * What this phone knows that the synced blob must not (ADR-042), for the rules that set a budget.
 * Pass it to `dailyTargets`, `energyBalance`, `dayLedger`, `deficitTarget` and `eatingEvidence`
 * wherever today's budget is shown.
 */
export function useDeviceAnswers(): DeviceAnswers {
  const safety = useKaya((s) => s.meta.eatingSafety);
  const gate = useKaya((s) => s.meta.trainingSafety?.gate);
  return useMemo(() => deviceAnswers(safety, gate), [safety, gate]);
}

export function deviceAnswers(safety: EatingSafety | undefined, trainingGate?: TrainingSafety["gate"]): DeviceAnswers {
  const out: DeviceAnswers = safety === "yes" ? { gentle: true } : {};
  if (trainingGate === "doctor") out.trainingGate = true;
  return out;
}

/** The exercise safety screens' answer, as the template filler reads it: the session and the
 *  player pass it, the food screens pass `useDeviceAnswers` (ADR-052, plan §3.5). */
export function useTrainingDevice(): TrainingDevice {
  const safety = useKaya((s) => s.meta.trainingSafety);
  return useMemo(() => trainingDevice(safety), [safety]);
}

/** The phone's player mode (Simple, voice, language): LocalMeta first, then what the answers set. */
export function usePlayerMode(): PlayerMode {
  const training = useKaya((s) => s.S.profile.training);
  const meta = useKaya((s) => s.meta);
  return useMemo(() => playerMode(training, meta), [training, meta]);
}

/** Why the treadmill runs its easy walk today, or null: a held plan or fast work off (P9). */
export function useEasyTreadmill(): string | null {
  const S = useKaya((s) => s.S);
  const answers = useDeviceAnswers();
  return useMemo(() => easyTreadmillLine(S, answers), [S, answers]);
}

export function trainingDevice(safety: TrainingSafety | undefined): TrainingDevice {
  if (!safety) return {};
  const out: TrainingDevice = {};
  if (safety.gate === "doctor") out.doctor = true;
  if (safety.recent && safety.recent !== "none") out.recent = true;
  return out;
}

/** The type scale reads its steps off `<html data-text-size>` (kaya.css); Normal is no attribute. */
function applyTextSize(size: TextSize | undefined) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!size || size === "normal") root.removeAttribute("data-text-size");
  else root.setAttribute("data-text-size", size);
}

/**
 * Whether the admin surfaces should be showing.
 *
 * Both halves matter: the account has to be an admin, and the admin has to not be previewing the
 * app as somebody else. Every admin entry point reads this rather than `user.role`.
 */
export const useIsAdmin = () =>
  useKaya((s) => s.user?.role === "admin" && !s.viewAsUser);

/** Convenience selectors used across screens. */
export const useState_ = () => useKaya((s) => s.S);
export const useStats = () => useKaya((s) => stats(s.S));
export const useTodayActive = () => useKaya((s) => dayActive(s.S, todayStr()));
export const useIsNeutralToday = () => useKaya((s) => neutralDay(s.S, todayStr()));
